import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    ArrowLeft, Loader2, Sparkles, Edit3, Mail, Phone, MapPin,
    Briefcase, GraduationCap, Award, Globe, Clock, Tag,
    MessageSquare, ChevronRight, Calendar, Star, User,
    Building2, DollarSign, Sun, Moon, Monitor, Send, Bot, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    active: 'bg-emerald-100 text-emerald-700',
    hired: 'bg-blue-100 text-blue-700',
    recall: 'bg-amber-100 text-amber-700',
    do_not_contact: 'bg-red-100 text-red-600',
    inactive: 'bg-stone-100 text-stone-500',
};

const PROFICIENCY_COLORS = {
    beginner: 'bg-stone-100 text-stone-600 border-stone-200',
    intermediate: 'bg-blue-50 text-blue-700 border-blue-200',
    advanced: 'bg-purple-50 text-purple-700 border-purple-200',
    expert: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SOURCE_LABELS = {
    job_board: 'Job Board',
    referral: 'Referral',
    import: 'Import',
    manual: 'Manual',
    linkedin: 'LinkedIn',
};

const AVAILABILITY_LABELS = {
    immediate: 'Immediate',
    '2_weeks': '2 Weeks',
    '1_month': '1 Month',
    other: 'Other',
};

const WORK_PREF_LABELS = {
    wfh: 'Work from Home',
    office: 'Office',
    hybrid: 'Hybrid',
};

const TABS = [
    { key: 'overview', label: 'Overview', icon: User },
    { key: 'work_history', label: 'Work History', icon: Briefcase },
    { key: 'skills_education', label: 'Skills & Education', icon: GraduationCap },
    { key: 'applications', label: 'Applications', icon: Building2 },
    { key: 'communications', label: 'Communications', icon: MessageSquare },
];

export default function ATSApplicantDetail() {
    const { applicantId } = useParams();
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [applicant, setApplicant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [suggestingJobs, setSuggestingJobs] = useState(false);
    const [suggestedJobs, setSuggestedJobs] = useState(null);
    const [showSuggestDialog, setShowSuggestDialog] = useState(false);

    const loadApplicant = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/applicants/${applicantId}`, { headers: getAuthHeader() });
            setApplicant(res.data);
        } catch { toast.error('Failed to load applicant'); navigate('/recruitment/talent-pool'); }
        finally { setLoading(false); }
    }, [applicantId, getAuthHeader, navigate]);

    useEffect(() => { loadApplicant(); }, [loadApplicant]);

    const handleSuggestJobs = async () => {
        setSuggestingJobs(true);
        setSuggestedJobs(null);
        setShowSuggestDialog(true);
        try {
            const res = await axios.post(`${API}/ats/applicants/${applicantId}/suggest-jobs`, {}, { headers: getAuthHeader() });
            setSuggestedJobs(res.data?.suggestions || res.data?.jobs || res.data || []);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to suggest jobs'); }
        finally { setSuggestingJobs(false); }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    if (!applicant) return null;

    const profile = applicant.profile || {};
    const applications = applicant.applications || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
                <button onClick={() => navigate('/recruitment/talent-pool')} className="mt-1 p-1 rounded hover:bg-stone-100">
                    <ArrowLeft className="w-5 h-5 text-stone-400" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-bold text-lg flex-shrink-0">
                            {(applicant.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-stone-900"
                                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                    {applicant.name}
                                </h1>
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[applicant.status] || 'bg-stone-100 text-stone-500'}`}>
                                    {applicant.status?.replace(/_/g, ' ')}
                                </span>
                            </div>
                            {profile.headline && (
                                <p className="text-sm text-stone-500 mt-0.5">{profile.headline}</p>
                            )}
                        </div>
                    </div>
                    {/* Profile completeness bar */}
                    {applicant.profile_completeness > 0 && (
                        <div className="flex items-center gap-3 mt-3 ml-[52px]">
                            <div className="flex-1 max-w-xs h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${
                                        applicant.profile_completeness >= 80 ? 'bg-emerald-500' :
                                        applicant.profile_completeness >= 50 ? 'bg-amber-500' : 'bg-red-400'
                                    }`}
                                    style={{ width: `${applicant.profile_completeness}%` }}
                                />
                            </div>
                            <span className="text-xs text-stone-500">{applicant.profile_completeness}% complete</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Button onClick={handleSuggestJobs} variant="outline" size="sm">
                        <Sparkles className="w-4 h-4 mr-1" /> Suggest Jobs
                    </Button>
                    <Button onClick={() => navigate(`/recruitment/talent-pool/${applicantId}/edit`)} variant="outline" size="sm">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit Profile
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200">
                <div className="flex gap-1">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                tab === t.key
                                    ? 'border-stone-900 text-stone-900'
                                    : 'border-transparent text-stone-500 hover:text-stone-700'
                            }`}>
                            <t.icon className="w-4 h-4" /> {t.label}
                            {t.key === 'applications' && applications.length > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-stone-100 text-xs">{applications.length}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {tab === 'overview' && <OverviewTab applicant={applicant} profile={profile} />}
            {tab === 'work_history' && <WorkHistoryTab workHistory={profile.work_history || []} />}
            {tab === 'skills_education' && <SkillsEducationTab profile={profile} />}
            {tab === 'applications' && <ApplicationsTab applications={applications} navigate={navigate} />}
            {tab === 'communications' && <CommunicationsTab applicantId={applicantId} applicant={applicant} getAuthHeader={getAuthHeader} />}

            {/* Suggest Jobs Dialog */}
            <Dialog open={showSuggestDialog} onOpenChange={setShowSuggestDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-amber-500" /> AI Job Suggestions
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        {suggestingJobs ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-stone-400 mb-3" />
                                <p className="text-sm text-stone-500">Analyzing profile and matching jobs...</p>
                            </div>
                        ) : suggestedJobs && suggestedJobs.length > 0 ? (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {suggestedJobs.map((job, i) => (
                                    <div key={job.job_id || i}
                                        onClick={() => { setShowSuggestDialog(false); navigate(`/recruitment/jobs/${job.job_id}`); }}
                                        className="bg-white rounded-lg border border-stone-200 p-3 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer">
                                        <div className="flex items-center justify-between">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-sm text-stone-900">{job.title || job.job_title}</p>
                                                {job.department && <p className="text-xs text-stone-400 mt-0.5">{job.department}</p>}
                                                {job.reason && <p className="text-xs text-stone-500 mt-1">{job.reason}</p>}
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                {job.match_score != null && (
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                                        job.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                                        job.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                                                        'bg-stone-100 text-stone-600'
                                                    }`}>
                                                        {job.match_score}%
                                                    </span>
                                                )}
                                                <ChevronRight className="w-4 h-4 text-stone-300" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : suggestedJobs ? (
                            <div className="text-center py-12">
                                <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                <p className="text-sm text-stone-500">No matching jobs found</p>
                                <p className="text-xs text-stone-400 mt-1">Try adding more profile details to improve matching</p>
                            </div>
                        ) : null}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSuggestDialog(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function OverviewTab({ applicant, profile }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-stone-400 flex-shrink-0" />
                        <a href={`mailto:${applicant.email}`} className="text-stone-700 hover:text-stone-900 truncate">{applicant.email}</a>
                    </div>
                    {applicant.phone && (
                        <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-700">{applicant.phone}</span>
                        </div>
                    )}
                    {applicant.whatsapp_number && (
                        <div className="flex items-center gap-2 text-sm">
                            <MessageSquare className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-700">{applicant.whatsapp_number}</span>
                            <span className="text-[10px] text-stone-400">WhatsApp</span>
                        </div>
                    )}
                    {profile.location && (profile.location.city || profile.location.country) && (
                        <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-700">
                                {[profile.location.city, profile.location.country].filter(Boolean).join(', ')}
                            </span>
                        </div>
                    )}
                    {profile.linkedin_url && (
                        <div className="flex items-center gap-2 text-sm">
                            <Globe className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 truncate">LinkedIn Profile</a>
                        </div>
                    )}
                </div>
            </div>

            {/* Preferences */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Preferences</h3>
                <div className="space-y-3">
                    {profile.availability && (
                        <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-500">Availability:</span>
                            <span className="font-medium text-stone-700">{AVAILABILITY_LABELS[profile.availability] || profile.availability}</span>
                        </div>
                    )}
                    {profile.work_preference && (
                        <div className="flex items-center gap-2 text-sm">
                            <Monitor className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-500">Work:</span>
                            <span className="font-medium text-stone-700">{WORK_PREF_LABELS[profile.work_preference] || profile.work_preference}</span>
                        </div>
                    )}
                    {profile.shift_preference?.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                            <Sun className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-500">Shift:</span>
                            <span className="font-medium text-stone-700">{profile.shift_preference.join(', ')}</span>
                        </div>
                    )}
                    {profile.desired_salary && (profile.desired_salary.min || profile.desired_salary.max) && (
                        <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-500">Salary:</span>
                            <span className="font-medium text-stone-700">
                                {profile.desired_salary.currency || ''}{' '}
                                {profile.desired_salary.min && profile.desired_salary.max
                                    ? `${profile.desired_salary.min.toLocaleString()} - ${profile.desired_salary.max.toLocaleString()}`
                                    : profile.desired_salary.min
                                        ? `${profile.desired_salary.min.toLocaleString()}+`
                                        : `up to ${profile.desired_salary.max.toLocaleString()}`
                                }
                            </span>
                        </div>
                    )}
                    {applicant.source && (
                        <div className="flex items-center gap-2 text-sm">
                            <Star className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <span className="text-stone-500">Source:</span>
                            <span className="font-medium text-stone-700">{SOURCE_LABELS[applicant.source] || applicant.source}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Tags</h3>
                {applicant.tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                        {applicant.tags.map(tag => (
                            <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-50 border border-stone-200 rounded-md text-xs text-stone-600">
                                <Tag className="w-3 h-3" />{tag}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No tags assigned</p>
                )}
                <div className="mt-4 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Dates</h4>
                    <div className="space-y-1 text-xs text-stone-500">
                        <div className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            <span>Added {new Date(applicant.created_at).toLocaleDateString()}</span>
                        </div>
                        {applicant.updated_at && (
                            <div className="flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" />
                                <span>Updated {new Date(applicant.updated_at).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary */}
            {(profile.summary || applicant.profile_md) && (
                <div className="lg:col-span-3 bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-3">Summary</h3>
                    {profile.summary && (
                        <p className="text-sm text-stone-600 leading-relaxed">{profile.summary}</p>
                    )}
                    {applicant.profile_md && (
                        <div className="mt-4 pt-4 border-t border-stone-100 prose prose-stone prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(applicant.profile_md.replace(/\n/g, '<br/>')) }} />
                    )}
                </div>
            )}
        </div>
    );
}

function WorkHistoryTab({ workHistory }) {
    if (workHistory.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No work history recorded</p>
                <p className="text-xs text-stone-400 mt-1">Work history will appear here once the profile is updated</p>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[19px] top-4 bottom-4 w-px bg-stone-200" />
            <div className="space-y-4">
                {workHistory.map((entry, i) => (
                    <div key={i} className="relative flex gap-4">
                        {/* Timeline dot */}
                        <div className="relative z-10 mt-4">
                            <div className={`w-[10px] h-[10px] rounded-full border-2 ${
                                i === 0 ? 'bg-stone-900 border-stone-900' : 'bg-white border-stone-300'
                            }`} style={{ marginLeft: '10px' }} />
                        </div>
                        {/* Card */}
                        <div className="flex-1 bg-white rounded-xl border border-stone-200 p-5 hover:shadow-sm transition-shadow">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h4 className="font-semibold text-stone-900 text-sm">{entry.title}</h4>
                                    <p className="text-sm text-stone-600 flex items-center gap-1 mt-0.5">
                                        <Building2 className="w-3.5 h-3.5 text-stone-400" />
                                        {entry.company}
                                    </p>
                                </div>
                                <span className="text-xs text-stone-400 flex-shrink-0">
                                    {entry.start_date || '?'} — {entry.end_date || 'Present'}
                                </span>
                            </div>
                            {entry.description && (
                                <p className="text-sm text-stone-500 mt-3 leading-relaxed">{entry.description}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SkillsEducationTab({ profile }) {
    const skills = profile.skills || [];
    const education = profile.education || [];
    const certifications = profile.certifications || [];
    const languages = profile.languages || [];

    return (
        <div className="space-y-6">
            {/* Skills */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Skills</h3>
                {skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {skills.map((skill, i) => (
                            <span key={i}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                                    PROFICIENCY_COLORS[skill.proficiency] || 'bg-stone-50 text-stone-600 border-stone-200'
                                }`}>
                                {skill.name}
                                {skill.proficiency && (
                                    <span className="opacity-60 text-[10px]">{skill.proficiency}</span>
                                )}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No skills recorded</p>
                )}
            </div>

            {/* Education */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Education</h3>
                {education.length > 0 ? (
                    <div className="space-y-3">
                        {education.map((edu, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                                <GraduationCap className="w-5 h-5 text-stone-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-sm text-stone-900">
                                        {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                                    </p>
                                    <p className="text-xs text-stone-500 mt-0.5">{edu.institution}</p>
                                    {edu.year && <p className="text-xs text-stone-400 mt-0.5">{edu.year}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No education recorded</p>
                )}
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Certifications</h3>
                {certifications.length > 0 ? (
                    <div className="space-y-2">
                        {certifications.map((cert, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                                <Award className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-stone-900">{cert.name}</p>
                                    <p className="text-xs text-stone-500">
                                        {cert.issuer}{cert.date ? ` — ${cert.date}` : ''}
                                    </p>
                                </div>
                                {cert.url && (
                                    <a href={cert.url} target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-700 flex-shrink-0">View</a>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No certifications recorded</p>
                )}
            </div>

            {/* Languages */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4">Languages</h3>
                {languages.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {languages.map((lang, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-lg text-sm">
                                <Globe className="w-3.5 h-3.5 text-stone-400" />
                                <span className="font-medium text-stone-700">{lang.name}</span>
                                {lang.level && <span className="text-xs text-stone-400">({lang.level})</span>}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No languages recorded</p>
                )}
            </div>
        </div>
    );
}

function ApplicationsTab({ applications, navigate }) {
    if (applications.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                <Building2 className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No applications yet</p>
                <p className="text-xs text-stone-400 mt-1">Applications will appear here when the applicant applies or is matched to jobs</p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {applications.map(app => (
                <div key={app.application_id}
                    onClick={() => navigate(`/recruitment/jobs/${app.job_id}`)}
                    className="bg-white rounded-xl border border-stone-200 p-4 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer">
                    <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-stone-900">{app.job_title}</span>
                                <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                    app.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                    app.status === 'hired' ? 'bg-blue-100 text-blue-700' :
                                    app.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                    'bg-stone-100 text-stone-500'
                                }`}>
                                    {app.status}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                                <span className="flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" />
                                    Stage: {app.stage_label || app.stage}
                                </span>
                                {app.created_at && (
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        Applied {new Date(app.created_at).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                        </div>
                        {app.ai_match_score != null && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="w-20 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${
                                        app.ai_match_score >= 80 ? 'bg-emerald-500' :
                                        app.ai_match_score >= 60 ? 'bg-amber-500' :
                                        'bg-red-400'
                                    }`} style={{ width: `${app.ai_match_score}%` }} />
                                </div>
                                <span className="text-xs font-semibold text-stone-600 w-8 text-right">{app.ai_match_score}%</span>
                            </div>
                        )}
                        <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function CommunicationsTab({ applicantId, applicant, getAuthHeader }) {
    const [comms, setComms] = useState([]);
    const [waThread, setWaThread] = useState([]);
    const [waStatus, setWaStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCompose, setShowCompose] = useState(false);
    const [sending, setSending] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [viewMode, setViewMode] = useState('timeline'); // timeline | whatsapp
    const [form, setForm] = useState({ subject: '', body: '', channel: 'email' });
    const [waMessage, setWaMessage] = useState('');

    const loadComms = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/communications?applicant_id=${applicantId}&page_size=100`, { headers: getAuthHeader() });
            setComms(res.data?.data || []);
        } catch { /* may not have comms yet */ }
        finally { setLoading(false); }
    }, [applicantId, getAuthHeader]);

    const loadWaThread = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/communications/whatsapp/thread/${applicantId}?page_size=200`, { headers: getAuthHeader() });
            setWaThread(res.data?.data || []);
        } catch { /* no WA thread yet */ }
    }, [applicantId, getAuthHeader]);

    const checkWaStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/communications/whatsapp/status`, { headers: getAuthHeader() });
            setWaStatus(res.data);
        } catch { setWaStatus({ connected: false }); }
    }, [getAuthHeader]);

    useEffect(() => { loadComms(); checkWaStatus(); }, [loadComms, checkWaStatus]);
    useEffect(() => { if (viewMode === 'whatsapp') loadWaThread(); }, [viewMode, loadWaThread]);

    const handleSend = async () => {
        if (!form.body.trim()) return toast.error('Message body is required');
        setSending(true);
        try {
            await axios.post(`${API}/ats/communications`, {
                applicant_id: applicantId,
                channel: form.channel,
                subject: form.subject,
                body: form.body,
            }, { headers: getAuthHeader() });
            toast.success('Message sent');
            setShowCompose(false);
            setForm({ subject: '', body: '', channel: 'email' });
            loadComms();
            if (viewMode === 'whatsapp') loadWaThread();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
        finally { setSending(false); }
    };

    const handleWaSend = async () => {
        if (!waMessage.trim()) return;
        setSending(true);
        try {
            await axios.post(`${API}/ats/communications`, {
                applicant_id: applicantId,
                channel: 'whatsapp',
                body: waMessage,
            }, { headers: getAuthHeader() });
            toast.success('WhatsApp message sent');
            setWaMessage('');
            loadWaThread();
            loadComms();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
        finally { setSending(false); }
    };

    const handleAIDraft = async (purpose) => {
        setDrafting(true);
        try {
            const res = await axios.post(`${API}/ats/communications/ai-draft`, {
                applicant_id: applicantId,
                purpose,
                channel: form.channel,
            }, { headers: getAuthHeader() });
            if (viewMode === 'whatsapp') {
                setWaMessage(res.data.body || '');
            } else {
                setForm(f => ({ ...f, subject: res.data.subject || '', body: res.data.body || '' }));
                setShowCompose(true);
            }
            toast.success('AI draft generated');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate draft'); }
        finally { setDrafting(false); }
    };

    const CHANNEL_ICONS = { email: Mail, whatsapp: Phone, portal: MessageSquare, system: Bot };
    const DIRECTION_COLORS = {
        outbound: 'border-l-blue-400',
        inbound: 'border-l-emerald-400',
    };

    const hasWaNumber = !!(applicant?.whatsapp_number || applicant?.phone);
    const waConnected = waStatus?.connected;

    if (loading) return (
        <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    return (
        <div className="space-y-4">
            {/* View Toggle + Actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
                    <button onClick={() => setViewMode('timeline')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'timeline' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                        <Mail className="w-3.5 h-3.5 inline mr-1" /> Timeline
                    </button>
                    <button onClick={() => setViewMode('whatsapp')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${viewMode === 'whatsapp' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                        <Phone className="w-3.5 h-3.5" /> WhatsApp
                        {waConnected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    </button>
                </div>
                <div className="flex gap-2">
                    {viewMode === 'timeline' && (
                        <>
                            <Button variant="outline" size="sm" onClick={() => handleAIDraft('follow_up')} disabled={drafting}>
                                {drafting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                                AI Draft
                            </Button>
                            <Button size="sm" onClick={() => setShowCompose(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                                <Send className="w-4 h-4 mr-1" /> New Message
                            </Button>
                        </>
                    )}
                    {viewMode === 'whatsapp' && (
                        <Button variant="outline" size="sm" onClick={loadWaThread}>
                            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                        </Button>
                    )}
                </div>
            </div>

            {/* ── Timeline View ── */}
            {viewMode === 'timeline' && (
                <>
                    {comms.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
                            <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                            <p className="text-stone-500 font-medium">No communications yet</p>
                            <p className="text-sm text-stone-400 mt-1">Send a message or generate an AI draft to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {comms.map(c => {
                                const ChannelIcon = CHANNEL_ICONS[c.channel] || MessageSquare;
                                const borderColor = DIRECTION_COLORS[c.direction] || 'border-l-stone-300';
                                return (
                                    <div key={c.comm_id} className={`bg-white rounded-xl border border-stone-200 border-l-4 ${borderColor} p-4`}>
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <ChannelIcon className="w-4 h-4 text-stone-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium text-stone-900">{c.from_name || c.from_type}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                        c.direction === 'outbound' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'
                                                    }`}>{c.direction}</span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-50 text-stone-500">{c.channel}</span>
                                                    {c.ai_generated && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 flex items-center gap-0.5">
                                                            <Sparkles className="w-2.5 h-2.5" /> AI
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-stone-400 ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                                                </div>
                                                {c.subject && <p className="text-sm font-medium text-stone-800 mt-1">{c.subject}</p>}
                                                <p className="text-sm text-stone-600 mt-1 whitespace-pre-wrap">{c.body}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* ── WhatsApp Chat View ── */}
            {viewMode === 'whatsapp' && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    {/* WA Header */}
                    <div className="bg-emerald-600 px-4 py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <Phone className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-white">{applicant?.name || 'Applicant'}</p>
                            <p className="text-xs text-emerald-100">{applicant?.whatsapp_number || applicant?.phone || 'No number'}</p>
                        </div>
                        {waConnected && (
                            <span className="ml-auto text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Connected</span>
                        )}
                    </div>

                    {!waConnected ? (
                        <div className="text-center py-12 px-4">
                            <Phone className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-stone-700">WhatsApp not connected</p>
                            <p className="text-xs text-stone-400 mt-1">Connect WhatsApp in Settings → Integrations to send messages</p>
                        </div>
                    ) : !hasWaNumber ? (
                        <div className="text-center py-12 px-4">
                            <Phone className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-stone-700">No WhatsApp number</p>
                            <p className="text-xs text-stone-400 mt-1">Add a WhatsApp number or phone to the applicant profile</p>
                        </div>
                    ) : (
                        <>
                            {/* Messages Area */}
                            <div className="h-[400px] overflow-y-auto p-4 space-y-2 bg-stone-50/50"
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0v60M0 30h60\' stroke=\'%23f5f5f4\' stroke-width=\'1\' fill=\'none\'/%3E%3C/svg%3E")' }}>
                                {waThread.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-xs text-stone-400">No messages yet. Send the first message below.</p>
                                    </div>
                                ) : (
                                    [...waThread].reverse().map(msg => (
                                        <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[75%] rounded-xl px-3 py-2 ${
                                                msg.direction === 'outbound'
                                                    ? 'bg-emerald-600 text-white rounded-br-sm'
                                                    : 'bg-white border border-stone-200 text-stone-800 rounded-bl-sm'
                                            }`}>
                                                <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                                                <div className={`flex items-center gap-1 mt-1 ${msg.direction === 'outbound' ? 'justify-end' : ''}`}>
                                                    {msg.ai_generated && (
                                                        <Sparkles className={`w-2.5 h-2.5 ${msg.direction === 'outbound' ? 'text-emerald-200' : 'text-amber-400'}`} />
                                                    )}
                                                    <span className={`text-[10px] ${msg.direction === 'outbound' ? 'text-emerald-200' : 'text-stone-400'}`}>
                                                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                    </span>
                                                    {msg.direction === 'outbound' && (
                                                        <span className={`text-[10px] ${msg.status === 'read' ? 'text-blue-300' : 'text-emerald-200'}`}>
                                                            {msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Composer */}
                            <div className="border-t border-stone-200 p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    {['follow_up', 'interview_prep', 'stage_update'].map(p => (
                                        <button key={p} onClick={() => { setForm(f => ({ ...f, channel: 'whatsapp' })); handleAIDraft(p); }} disabled={drafting}
                                            className="px-2 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors flex items-center gap-1">
                                            <Sparkles className="w-2.5 h-2.5" /> {p.replace(/_/g, ' ')}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-end gap-2">
                                    <textarea
                                        value={waMessage}
                                        onChange={e => setWaMessage(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleWaSend(); } }}
                                        placeholder="Type a message..."
                                        rows={1}
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                        style={{ minHeight: 38, maxHeight: 120 }}
                                    />
                                    <button
                                        onClick={handleWaSend}
                                        disabled={sending || !waMessage.trim()}
                                        className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 disabled:opacity-40 transition-colors flex-shrink-0"
                                    >
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Compose Dialog (for Timeline view) */}
            <Dialog open={showCompose} onOpenChange={setShowCompose}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>New Message</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="flex gap-2">
                            {['email', 'whatsapp'].map(ch => (
                                <button key={ch} onClick={() => setForm(f => ({ ...f, channel: ch }))}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                        form.channel === ch ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                                    }`}>
                                    {ch === 'email' ? 'Email' : 'WhatsApp'}
                                </button>
                            ))}
                        </div>
                        {form.channel === 'email' && (
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Subject</label>
                                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Message</label>
                            <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={6}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none" />
                        </div>
                        <div className="flex gap-2">
                            {['follow_up', 'interview_prep', 'stage_update'].map(p => (
                                <button key={p} onClick={() => handleAIDraft(p)} disabled={drafting}
                                    className="px-2 py-1 text-[10px] font-medium rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors flex items-center gap-1">
                                    <Sparkles className="w-2.5 h-2.5" /> {p.replace(/_/g, ' ')}
                                </button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCompose(false)}>Cancel</Button>
                        <Button onClick={handleSend} disabled={sending} className="bg-stone-900 text-white hover:bg-stone-800">
                            {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
